import React, { useState, useMemo } from 'react';
import { AccumulatedNode, AccumulatedEdge } from '../../types';
import { NODE_COLORS, IOC_COLORS } from '../../lib/entityColors';
import { classifyIOC, type IOCType } from '../../lib/iocClassifier';
import {
  EDGE_COLORS,
  SOURCE_COLORS,
  KNOWLEDGE_SOURCE_LABELS,
  resolveKnowledgeSource,
  type KnowledgeSource,
  type EdgeColorMode,
} from './useForceGraph';

const ALL_TYPES = ['MalwareFamily', 'ThreatActor', 'Country', 'Document', 'CVE', 'IOC', 'AttackTechnique', 'WebSource'] as const;
const IOC_SUB_LABELS: { key: IOCType; label: string }[] = [
  { key: 'HASH', label: 'Hash' },
  { key: 'IP', label: 'IP' },
  { key: 'DOMAIN', label: 'Domain' },
  { key: 'URL', label: 'URL' },
  { key: 'EMAIL', label: 'Email' },
];

// Stable ordering for source legend
const SOURCE_ORDER: KnowledgeSource[] = ['malpedia', 'mitre_attack', 'blog_knowledge', 'enrichment', 'computed'];

interface GraphLegendPanelProps {
  nodes: AccumulatedNode[];
  edges?: AccumulatedEdge[] | { rel_type: string; data_source?: string }[];
  typeFilters: Record<string, boolean>;
  onToggleTypeFilter: (type: string) => void;
  onShowAll: () => void;
  onHideAll?: () => void;
  edgeColorMode?: EdgeColorMode;
  onToggleEdgeColorMode?: () => void;
  // IOC sub-type filtering
  iocSubTypeFilters?: Record<IOCType, boolean>;
  onToggleIocSubType?: (type: IOCType) => void;
  // Date range filtering
  dateFrom?: string;
  dateTo?: string;
  onDateRangeChange?: (from: string, to: string) => void;
}

export function GraphLegendPanel({
  nodes,
  edges,
  typeFilters,
  onToggleTypeFilter,
  onShowAll,
  onHideAll,
  edgeColorMode = 'source',
  onToggleEdgeColorMode,
  iocSubTypeFilters,
  onToggleIocSubType,
  dateFrom,
  dateTo,
  onDateRangeChange,
}: GraphLegendPanelProps) {
  const [collapsed, setCollapsed] = useState(true);

  const { counts, iocSubCounts } = useMemo(() => {
    const map: Record<string, number> = {};
    const iocSub: Record<IOCType, number> = { HASH: 0, IP: 0, DOMAIN: 0, URL: 0, EMAIL: 0, UNKNOWN: 0 };
    for (const t of ALL_TYPES) map[t] = 0;
    for (const n of nodes) {
      if (n.label in map) map[n.label]++;
      if (n.label === 'IOC') {
        iocSub[classifyIOC(n.name)]++;
      }
    }
    return { counts: map, iocSubCounts: iocSub };
  }, [nodes]);

  // Edge counts by rel_type (for rel_type mode)
  const edgeCounts = useMemo(() => {
    if (!edges || edges.length === 0) return [];
    const map = new Map<string, number>();
    for (const e of edges) {
      map.set(e.rel_type, (map.get(e.rel_type) || 0) + 1);
    }
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([rel_type, count]) => ({ rel_type, count }));
  }, [edges]);

  // Edge counts by knowledge source (for source mode)
  const sourceCounts = useMemo(() => {
    if (!edges || edges.length === 0) return [];
    const map = new Map<KnowledgeSource, number>();
    for (const e of edges) {
      const ks = resolveKnowledgeSource(e.rel_type, (e as AccumulatedEdge).data_source);
      map.set(ks, (map.get(ks) || 0) + 1);
    }
    return SOURCE_ORDER
      .filter(s => map.has(s))
      .map(source => ({ source, count: map.get(source)! }));
  }, [edges]);

  if (collapsed) {
    return (
      <div className="malpedia-graph-legend-panel malpedia-graph-legend-panel--collapsed">
        <button
          className="malpedia-graph-legend-toggle"
          onClick={() => setCollapsed(false)}
          title="Show legend"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <rect x="1" y="1" width="5" height="5" rx="1" />
            <rect x="1" y="10" width="5" height="5" rx="1" />
            <rect x="10" y="1" width="5" height="5" rx="1" />
            <rect x="10" y="10" width="5" height="5" rx="1" />
          </svg>
        </button>
      </div>
    );
  }

  const hasEdges = edgeCounts.length > 0 || sourceCounts.length > 0;

  return (
    <div className="malpedia-graph-legend-panel">
      <div className="malpedia-graph-legend-header">
        <span className="malpedia-graph-legend-title">Legend</span>
        <button
          className="malpedia-graph-legend-toggle"
          onClick={() => setCollapsed(true)}
          title="Hide legend"
        >
          &#x2715;
        </button>
      </div>
      <div className="malpedia-graph-legend-content">
        {ALL_TYPES.map((type) => {
          const count = counts[type] || 0;
          const visible = typeFilters[type] !== false;
          const disabled = count === 0;

          return (
            <React.Fragment key={type}>
              <button
                className={
                  'malpedia-graph-legend-row' +
                  (disabled ? ' malpedia-graph-legend-row--disabled' : '') +
                  (!visible ? ' malpedia-graph-legend-row--hidden' : '')
                }
                onClick={() => !disabled && onToggleTypeFilter(type)}
                disabled={disabled}
              >
                <span
                  className="malpedia-graph-legend-dot"
                  style={
                    visible && !disabled
                      ? { backgroundColor: NODE_COLORS[type] }
                      : { border: `2px solid ${NODE_COLORS[type]}`, backgroundColor: 'transparent' }
                  }
                />
                <span className="malpedia-graph-legend-label">{type}</span>
                <span className="malpedia-graph-legend-count">({count})</span>
              </button>
              {type === 'IOC' && count > 0 && visible && (
                <>
                  {IOC_SUB_LABELS.map(({ key, label }) => {
                    const subCount = iocSubCounts[key];
                    if (subCount === 0) return null;
                    const subVisible = iocSubTypeFilters ? iocSubTypeFilters[key] !== false : true;
                    const canToggle = !!onToggleIocSubType;
                    return (
                      <button
                        key={key}
                        className={
                          'malpedia-graph-legend-row malpedia-graph-legend-row--sub' +
                          (!subVisible ? ' malpedia-graph-legend-row--hidden' : '')
                        }
                        onClick={() => canToggle && onToggleIocSubType!(key)}
                        disabled={!canToggle}
                      >
                        <span
                          className="malpedia-graph-legend-dot malpedia-graph-legend-dot--small"
                          style={
                            subVisible
                              ? { backgroundColor: IOC_COLORS[key] }
                              : { border: `2px solid ${IOC_COLORS[key]}`, backgroundColor: 'transparent' }
                          }
                        />
                        <span className="malpedia-graph-legend-label">{label}</span>
                        <span className="malpedia-graph-legend-count">({subCount})</span>
                      </button>
                    );
                  })}
                  {onToggleIocSubType && iocSubTypeFilters && (
                    <button
                      className="malpedia-graph-legend-row malpedia-graph-legend-row--sub malpedia-graph-legend-row--toggle-all"
                      onClick={() => {
                        const allVisible = IOC_SUB_LABELS.every(({ key }) => iocSubTypeFilters[key] !== false);
                        for (const { key } of IOC_SUB_LABELS) {
                          if (allVisible && iocSubTypeFilters[key] !== false) onToggleIocSubType(key);
                          if (!allVisible && iocSubTypeFilters[key] === false) onToggleIocSubType(key);
                        }
                      }}
                    >
                      <span className="malpedia-graph-legend-label" style={{ fontSize: '10px', opacity: 0.7 }}>
                        {IOC_SUB_LABELS.every(({ key }) => iocSubTypeFilters[key] !== false)
                          ? 'Hide All IOC Types'
                          : 'Show All IOC Types'}
                      </span>
                    </button>
                  )}
                </>
              )}
            </React.Fragment>
          );
        })}

        {hasEdges && (
          <>
            <div className="malpedia-graph-legend-divider" />

            {/* Edge color mode toggle */}
            {onToggleEdgeColorMode && (
              <div className="malpedia-graph-legend-edge-header">
                <span className="malpedia-graph-legend-edge-title">Edges</span>
                <div className="malpedia-graph-legend-edge-toggle">
                  <button
                    className={edgeColorMode === 'source' ? 'active' : ''}
                    onClick={onToggleEdgeColorMode}
                  >
                    Source
                  </button>
                  <button
                    className={edgeColorMode === 'rel_type' ? 'active' : ''}
                    onClick={onToggleEdgeColorMode}
                  >
                    Type
                  </button>
                </div>
              </div>
            )}

            {edgeColorMode === 'source' ? (
              /* Source-grouped edge swatches */
              sourceCounts.map(({ source, count }) => (
                <div key={source} className="malpedia-graph-legend-row malpedia-graph-legend-row--edge">
                  <span
                    className="malpedia-graph-legend-edge-swatch"
                    style={{ backgroundColor: SOURCE_COLORS[source].swatch }}
                  />
                  <span className="malpedia-graph-legend-label">{KNOWLEDGE_SOURCE_LABELS[source]}</span>
                  <span className="malpedia-graph-legend-count">({count})</span>
                </div>
              ))
            ) : (
              /* Per-rel_type edge swatches */
              edgeCounts.map(({ rel_type, count }) => {
                const colors = EDGE_COLORS[rel_type];
                const lineColor = colors ? colors.particle : 'rgba(255, 255, 255, 0.4)';
                return (
                  <div key={rel_type} className="malpedia-graph-legend-row malpedia-graph-legend-row--edge">
                    <span className="malpedia-graph-legend-edge-swatch" style={{ backgroundColor: lineColor }} />
                    <span className="malpedia-graph-legend-label">{rel_type}</span>
                    <span className="malpedia-graph-legend-count">({count})</span>
                  </div>
                );
              })
            )}
          </>
        )}
      </div>
      {onDateRangeChange && (
        <>
          <div className="malpedia-graph-legend-divider" />
          <div className="malpedia-graph-legend-date-section">
            <div className="malpedia-graph-legend-date-title">Date Range</div>
            <div className="malpedia-graph-legend-date-row">
              <input
                type="date"
                className="malpedia-graph-legend-date-input"
                value={dateFrom || ''}
                onChange={e => onDateRangeChange(e.target.value, dateTo || '')}
              />
              <span className="malpedia-graph-legend-date-sep">--</span>
              <input
                type="date"
                className="malpedia-graph-legend-date-input"
                value={dateTo || ''}
                onChange={e => onDateRangeChange(dateFrom || '', e.target.value)}
              />
            </div>
            {(dateFrom || dateTo) && (
              <button
                className="malpedia-graph-legend-date-clear"
                onClick={() => onDateRangeChange('', '')}
              >
                Clear dates
              </button>
            )}
          </div>
        </>
      )}

      <div className="malpedia-graph-legend-footer">
        <button onClick={onShowAll}>Show All</button>
      </div>
    </div>
  );
}
